import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Printer as PrinterIcon,
  AlertCircle,
  Search,
  QrCode,
  ArrowLeft,
  CheckCircle,
  FileText,
  Receipt
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from 'react-i18next';
import type { Database } from '../lib/database.types';
import { useBusinessProfile } from '../lib/businessProfileContext';
import SMSStatusBadge from '../components/SMSStatusBadge';
import QRCode from 'qrcode';
import { formatPhoneNumber, formatDateTime, getRepairStageDates } from '../lib/utils';
import { jsPDF } from 'jspdf';

type Repair = Database['public']['Tables']['repairs']['Row'];
type Customer = Database['public']['Tables']['customers']['Row'];
type BusinessProfile = Database['public']['Tables']['business_profiles']['Row'];
type RepairStatusHistory = Database['public']['Tables']['repair_status_history']['Row'];

type RepairWithCustomer = Repair & {
  customer: Customer;
  diagnostic?: Database['public']['Tables']['diagnostics']['Row'][];
};

// Helper function to capitalize device type (e.g., "smartfon" -> "Smartfon")
const capitalizeDeviceType = (deviceType: string): string => {
  if (!deviceType) return deviceType;
  // Important: do NOT lowercase the rest (models/brands can be case-sensitive).
  return deviceType.charAt(0).toUpperCase() + deviceType.slice(1);
};

const LabelPreview = ({ repair, businessName, businessProfile }: { 
  repair: RepairWithCustomer; 
  businessName: string;
  businessProfile: BusinessProfile | null;
}) => {
  return (
        <div className="border rounded-lg p-6 bg-white shadow-inner">
          <div className="max-w-[90mm] mx-auto aspect-[9/5] bg-white border shadow-lg p-3">
            {/* Header with business name and phone */}
            <div className="text-center mb-3">
              <h3 className="text-sm font-bold uppercase">{businessName}</h3>
              <div className="text-xs">Tel: {businessProfile?.phone || '+998 XX XXX XX XX'}</div>
            </div>

        {/* Main content with QR code */}
        <div className="flex justify-between items-start mb-2">
          <div className="flex-1">
            <div className="text-sm font-bold mb-1">Tiket ID: {repair.repair_id}</div>
            <div className="text-xs mb-1">
              <strong>Sana:</strong> {new Date(repair.created_at).toLocaleDateString('uz-UZ')}
            </div>
            <div className="text-xs mb-1">
              <strong>Qurilma:</strong> {capitalizeDeviceType(repair.device_type)}
              {repair.device_model && ` - ${repair.device_model}`}
            </div>
          </div>
          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center ml-2">
            <QrCode className="w-12 h-12 text-gray-400" />
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t pt-2 mt-2">
          <div className="text-xs text-right">
            Yozuvning holatini kuzatish uchun skaner
          </div>
        </div>
      </div>
    </div>
  );
};

const LabelGeneration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [repair, setRepair] = useState<RepairWithCustomer | null>(null);
  const [latestRepairs, setLatestRepairs] = useState<RepairWithCustomer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'customer' | 'repair'>('customer');
  const [generating, setGenerating] = useState(false);
  const [generatingTalon, setGeneratingTalon] = useState(false);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const { businessProfile } = useBusinessProfile();
  const [completionRecord, setCompletionRecord] = useState<Database['public']['Tables']['completion_records']['Row'] | null>(null);
  const [statusHistory, setStatusHistory] = useState<RepairStatusHistory[] | null>(null);


  useEffect(() => {
    const fetchLatestRepairs = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('repairs')
          .select(`
            *,
            customer:customers(*)
          `)
          .order('created_at', { ascending: false })
          .limit(5);

        if (fetchError) throw fetchError;
        setLatestRepairs(data as RepairWithCustomer[] || []);
      } catch (err) {
        console.error('Error fetching latest repairs:', err);
      }
    };

    if (!id) {
      fetchLatestRepairs();
    }
  }, [id]);

  useEffect(() => {
    const fetchRepair = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('repairs')
          .select(`
            *,
            customer:customers(*),
            diagnostic:diagnostics(*)
          `)
          .eq('repair_id', id)
          .single();

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Repair not found');

        setRepair(data as RepairWithCustomer);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load repair details');
      } finally {
        setLoading(false);
      }
    };

    fetchRepair();
  }, [id]);

  useEffect(() => {
    const fetchCompletionRecord = async () => {
      if (!repair) {
        setCompletionRecord(null);
        setStatusHistory(null);
        return;
      }

      try {
        const [{ data: completionData, error: completionError }, { data: historyData, error: historyError }] =
          await Promise.all([
            supabase
              .from('completion_records')
              .select('*')
              .eq('repair_id', repair.id)
              .maybeSingle(),
            supabase
              .from('repair_status_history')
              .select('*')
              .eq('repair_id', repair.id)
              .order('changed_at', { ascending: true }),
          ]);

        if (!completionError) {
          setCompletionRecord(completionData || null);
        } else {
          setCompletionRecord(null);
        }

        if (!historyError) {
          setStatusHistory(historyData || null);
        } else {
          setStatusHistory(null);
        }
      } catch (err) {
        console.error('Error fetching completion record:', err);
        setCompletionRecord(null);
        setStatusHistory(null);
      }
    };
    
    fetchCompletionRecord();
  }, [repair]);

  const getNotesHistory = () => {
    if (!repair) return [];

    const history: Array<{
      stage: string;
      notes: string;
      date: string;
      icon: React.ReactNode;
    }> = [];

    // Intake notes (issue description)
    if (repair.issue_description) {
      history.push({
        stage: 'Qabul qilish',
        notes: repair.issue_description,
        date: repair.created_at,
        icon: <FileText className="w-4 h-4" />
      });
    }

    // Diagnostic notes
    if (repair.diagnostic && repair.diagnostic.length > 0) {
      repair.diagnostic.forEach((diagnostic) => {
        if (diagnostic.notes) {
          history.push({
            stage: 'Diagnostika',
            notes: diagnostic.notes,
            date: diagnostic.created_at,
            icon: <Search className="w-4 h-4" />
          });
        }
      });
    }

    // Completion notes
    if (completionRecord?.notes) {
      history.push({
        stage: 'Yakunlash',
        notes: completionRecord.notes,
        date: completionRecord.completed_at,
        icon: <CheckCircle className="w-4 h-4" />
      });
    }

    // Pickup stage (Topshirish)
    if (repair.status === 'picked' || repair.status === 'completed') {
      history.push({
        stage: 'Topshirish',
        notes: '',
        date: repair.updated_at,
        icon: <CheckCircle className="w-4 h-4" />
      });
    }

    // Cancellation notes
    if (repair.cancellation_note) {
      history.push({
        stage: 'Bekor qilish',
        notes: repair.cancellation_note,
        date: repair.updated_at,
        icon: <AlertCircle className="w-4 h-4" />
      });
    }

    // Sort by date
    return history.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);

    if (query.length < 2) {
      setLatestRepairs([]);
      return;
    }

    try {
      let queryBuilder = supabase.from('repairs');

      if (searchType === 'customer') {
        queryBuilder = queryBuilder
          .select(`
            *,
            customer:customers!inner(*)
          `)
          .ilike('customer.name', `%${query}%`);
      } else {
        queryBuilder = queryBuilder
          .select(`
            *,
            customer:customers(*)
          `)
          .ilike('repair_id', `%${query}%`);
      }

      const { data, error: searchError } = await queryBuilder
        .order('created_at', { ascending: false })
        .limit(5);

      if (searchError) throw searchError;
      setLatestRepairs(data as RepairWithCustomer[] || []);
    } catch (err) {
      console.error('Error searching repairs:', err);
    }
  };

  const generatePDF = async () => {
    if (!repair) return;

    setGenerating(true);
    try {
      // Generate QR code
      const businessId = businessProfile?.id;
      if (!businessId) {
        throw new Error('Business profile not found');
      }
      const labelUrl = `${window.location.origin}/shop/${businessId}`;
      const qrDataUrl = await QRCode.toDataURL(labelUrl, {
        width: 200,
        margin: 0,
      });

      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (!printWindow) return;

      const companyName = businessProfile?.master_name || 'MasterProUZ';
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print Label - ${repair.repair_id}</title>
            <style>
              @page { size: 70mm 30mm; margin: 0; }
              html, body {
                width: 70mm;
                height: 30mm;
                margin: 0;
                padding: 0;
                background: white;
              }
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: Arial, sans-serif; }

              .label-container {
                width: 70mm; 
                height: 30mm; 
                padding: 1mm 2mm 1.5mm 2mm; 
                overflow: hidden;
                display: flex;
                flex-direction: column;
              }
              
              .header { 
                text-align: center;
                margin-bottom: 2mm; 
              }
              
              .company-info h3 { 
                font-size: 4mm; 
                font-weight: 700; 
                text-transform: uppercase; 
                line-height: 1.1;
                margin: 0;
              }
              
              .company-info .phone { 
                font-size: 3mm; 
                color: #666; 
                margin-top: 0.5mm;
              }

              .main-content { 
                display: flex; 
                justify-content: space-between; 
                align-items: flex-start; 
                margin-bottom: 1mm;
                flex: 1;
              }
              
              .repair-info { 
                flex: 1; 
                min-width: 0;
              }
              
              .ticket-id { 
                font-size: 3.5mm; 
                font-weight: 700; 
                margin-bottom: 1.2mm; 
              }
              
              .info-line { 
                font-size: 2.8mm; 
                line-height: 1.2;
                margin-bottom: 0.8mm;
              }

              .qr-code { 
                width: 20mm; 
                height: 20mm; 
                background: #f5f5f5; 
                border-radius: 1.5mm; 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                margin-left: 2.5mm;
                flex-shrink: 0;
              }
              
              .qr-code img { 
                width: 18mm; 
                height: 18mm; 
              }

              .footer { 
                border-top: 0.3mm solid #000; 
                padding-top: 0.5mm; 
                margin-top: auto;
              }
              
              .footer-text { 
                font-size: 2mm; 
                text-align: center; 
                color: #000; 
              }

              /* Ensure single page with no accidental breaks */
              .label-container, .main-content { page-break-inside: avoid; }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="header">
                <div class="company-info">
                  <h3>${companyName}</h3>
                  <div class="phone">Tel: ${businessProfile?.phone || '+998 XX XXX XX XX'}</div>
                </div>
              </div>
              
              <div class="main-content">
                <div class="repair-info">
                  <div class="ticket-id">Tiket ID: ${repair.repair_id}</div>
                  <div class="info-line"><strong>Sana:</strong> ${new Date(repair.created_at).toLocaleDateString('uz-UZ')}</div>
                  <div class="info-line"><strong>Qurilma:</strong> ${capitalizeDeviceType(repair.device_type)}${repair.device_model ? ` - ${repair.device_model}` : ''}</div>
                </div>
                <div class="qr-code">
                  <img src="${qrDataUrl}" alt="QR Code" />
                </div>
              </div>
              
              <div class="footer">
                <div class="footer-text">Yozuvning holatini kuzatish uchun skaner</div>
              </div>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for the content to load, then trigger print
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        
        // Close the print window after printing
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      };
    } catch (err) {
      console.error('Error generating print:', err);
    } finally {
      setGenerating(false);
    }
  };

  const generateLabelPDF = async () => {
    if (!repair || !businessProfile || !repair.customer) return;

    setGeneratingLabel(true);
    try {
      const widthMm = 60;
      const heightMm = 40;

      // Generate Service Label PDF (Yorliq - for attaching to device)
      const serviceDoc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [widthMm, heightMm]
      });

      serviceDoc.setFont('helvetica', 'normal');

      // Customer name - Header (right side, bold, with proper wrapping)
      serviceDoc.setFontSize(11.5); // ~15% bigger than 10
      serviceDoc.setFont('helvetica', 'bold');
      const customerName = repair.customer.name || 'Mijoz';
      const customerNameLines = serviceDoc.splitTextToSize(customerName, widthMm / 2 - 3);
      let customerNameY = 5;
      customerNameLines.forEach((line: string, index: number) => {
        serviceDoc.text(line, widthMm - 3, customerNameY + (index * 3.5), { align: 'right', maxWidth: widthMm / 2 - 3 });
      });
      const customerNameHeight = customerNameLines.length * 3.5;

      // Customer phone (left side)
      serviceDoc.setFontSize(8); // ~15% bigger than 6.875
      serviceDoc.setFont('helvetica', 'normal');
      const customerPhoneText = `Tel: ${repair.customer.phone || 'N/A'}`;
      const customerPhoneY = customerNameY + customerNameHeight + 1.5;
      serviceDoc.text(customerPhoneText, 3, customerPhoneY, { maxWidth: widthMm / 2 - 3 });

      // Date and Device - Qurilma on left, Sana on right
      serviceDoc.setFontSize(7.2); // ~15% bigger than 6.25
      const dateText = new Date(repair.created_at).toLocaleDateString('uz-UZ');
      const capitalizedDeviceType = capitalizeDeviceType(repair.device_type);
      const deviceText = `${capitalizedDeviceType}${repair.device_model ? ` - ${repair.device_model}` : ''}`;
      
      // First row: Labels (with more space from Ticket ID)
      serviceDoc.setFont('helvetica', 'bold');
      serviceDoc.text('Qurilma:', 3, 12.5);
      serviceDoc.text('Sana:', widthMm - 3, 12.5, { align: 'right' });
      
      // Second row: Values
      serviceDoc.setFont('helvetica', 'normal');
      const deviceLines = serviceDoc.splitTextToSize(deviceText, widthMm / 2 - 3);
      const dateLines = serviceDoc.splitTextToSize(dateText, widthMm / 2 - 3);
      
      // Constants for layout calculation
      const valuesY = 15;
      const valueLineHeight = 2.8;
      const afterValuesGap = 1.0;
      
      // Calculate how many lines the device/date block takes
      const valuesBlockLines = Math.max(deviceLines.length, dateLines.length);
      
      // Device text on left
      if (deviceLines.length === 1) {
        serviceDoc.text(deviceLines[0], 3, valuesY);
      } else {
        deviceLines.forEach((line: string, index: number) => {
          serviceDoc.text(line, 3, valuesY + (index * valueLineHeight));
        });
      }
      
      // Date text on right
      if (dateLines.length === 1) {
        serviceDoc.text(dateLines[0], widthMm - 3, valuesY, { align: 'right' });
      } else {
        dateLines.forEach((line: string, index: number) => {
          serviceDoc.text(line, widthMm - 3, valuesY + (index * valueLineHeight), { align: 'right' });
        });
      }

      // Problem section - dynamically positioned based on device/date block height
      const muammoLabelY = valuesY + (valuesBlockLines - 1) * valueLineHeight + afterValuesGap + 2.5;
      serviceDoc.setFontSize(7.2); // match date/device size
      serviceDoc.setFont('helvetica', 'bold');
      serviceDoc.text('Muammo:', 3, muammoLabelY);
      
      serviceDoc.setFont('helvetica', 'normal');
      serviceDoc.setFontSize(7.2);
      const serviceProblemText = repair.issue_description || '—';
      const serviceProblemLines = serviceDoc.splitTextToSize(serviceProblemText, widthMm - 6);
      let serviceYPos = muammoLabelY + 2.5;
      serviceProblemLines.forEach((line: string) => {
        if (serviceYPos < heightMm - 6) {
          serviceDoc.text(line, 3, serviceYPos);
          serviceYPos += 5.625;
        }
      });

      // Ticket ID - bottom row, right side
      serviceDoc.setFontSize(8.6); // ~15% bigger than 7.5
      serviceDoc.setFont('helvetica', 'bold');
      const serviceTicketIdText = `Tiket ID: ${repair.repair_id}`;
      serviceDoc.text(serviceTicketIdText, widthMm - 3, heightMm - 3, { align: 'right' });

      // Price (if available) - at bottom left
      if (repair.price !== null && repair.price !== undefined) {
        serviceDoc.setFontSize(8.6);
        serviceDoc.setFont('helvetica', 'bold');
        const servicePriceText = `Narx: ${repair.price.toLocaleString('uz-UZ')} UZS`;
        serviceDoc.text(servicePriceText, 3, heightMm - 3);
      }

      // Save service label PDF
      serviceDoc.save(`yorliq-service-${repair.repair_id}.pdf`);

    } catch (err) {
      console.error('Error generating label PDF:', err);
      alert('Yorliq yaratishda xatolik yuz berdi');
    } finally {
      setGeneratingLabel(false);
    }
  };

  const generateTalonPDF = async () => {
    if (!repair || !businessProfile) return;

    setGeneratingTalon(true);
    try {
      // Generate QR code (same as in generatePDF)
      const businessId = businessProfile?.id;
      if (!businessId) {
        throw new Error('Business profile not found');
      }
      const labelUrl = `${window.location.origin}/shop/${businessId}`;
      const qrDataUrl = await QRCode.toDataURL(labelUrl, {
        width: 200,
        margin: 0,
      });

      // Create PDF with 60x40mm dimensions (landscape for thermal printer)
      const widthMm = 60;
      const heightMm = 40;

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [widthMm, heightMm]
      });

      doc.setFont('helvetica', 'normal');

      // Service name - Header (single line, centered, fit-to-width)
      const serviceNameRaw = businessProfile?.business_name || businessProfile?.master_name || 'Xizmat';
      const serviceName = serviceNameRaw.replace(/\s+/g, ' ').trim();
      const headerY = 5;
      const headerMaxWidth = widthMm - 6; // 3mm padding on each side
      let headerFontSize = 11.5; // ~15% bigger than 10
      const headerMinFontSize = 6;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(headerFontSize);
      while (doc.getTextWidth(serviceName) > headerMaxWidth && headerFontSize > headerMinFontSize) {
        headerFontSize -= 0.5;
        doc.setFontSize(headerFontSize);
      }

      doc.text(serviceName, widthMm / 2, headerY, { align: 'center' });
      const serviceNameHeight = 3.5; // single-line height baseline
      const serviceNameY = headerY;

      // Phone number (left side)
      doc.setFontSize(8); // ~15% bigger than 6.875
      doc.setFont('helvetica', 'normal');
      const phoneText = `Tel: ${businessProfile?.phone || 'N/A'}`;
      const phoneY = serviceNameY + serviceNameHeight + 1.5;
      doc.text(phoneText, 3, phoneY, { maxWidth: widthMm / 2 - 3 });

      // Date and Device - Qurilma on left, Sana on right
      doc.setFontSize(7.2); // ~15% bigger than 6.25
      const dateText = new Date(repair.created_at).toLocaleDateString('uz-UZ');
      const capitalizedDeviceType = capitalizeDeviceType(repair.device_type);
      const deviceText = `${capitalizedDeviceType}${repair.device_model ? ` - ${repair.device_model}` : ''}`;
      
      // First row: Labels (with more space from Ticket ID)
      doc.setFont('helvetica', 'bold');
      doc.text('Qurilma:', 3, 12.5);
      doc.text('Sana:', widthMm - 3, 12.5, { align: 'right' });
      
      // Second row: Values
      doc.setFont('helvetica', 'normal');
      const deviceLines = doc.splitTextToSize(deviceText, widthMm / 2 - 3);
      const dateLines = doc.splitTextToSize(dateText, widthMm / 2 - 3);
      
      // Constants for layout calculation
      const valuesY = 15;
      const valueLineHeight = 2.8;
      const afterValuesGap = 1.0;
      
      // Calculate how many lines the device/date block takes
      const valuesBlockLines = Math.max(deviceLines.length, dateLines.length);
      
      // Device text on left
      if (deviceLines.length === 1) {
        doc.text(deviceLines[0], 3, valuesY);
      } else {
        deviceLines.forEach((line: string, index: number) => {
          doc.text(line, 3, valuesY + (index * valueLineHeight));
        });
      }
      
      // Date text on right
      if (dateLines.length === 1) {
        doc.text(dateLines[0], widthMm - 3, valuesY, { align: 'right' });
      } else {
        dateLines.forEach((line: string, index: number) => {
          doc.text(line, widthMm - 3, valuesY + (index * valueLineHeight), { align: 'right' });
        });
      }

      // Problem description - dynamically positioned based on device/date block height
      const muammoLabelY = valuesY + (valuesBlockLines - 1) * valueLineHeight + afterValuesGap + 2.5;
      doc.setFontSize(7.2);
      doc.setFont('helvetica', 'bold');
      doc.text('Muammo:', 3, muammoLabelY);
      doc.setFont('helvetica', 'normal');
      const problemText = repair.issue_description || '—';
      const problemLines = doc.splitTextToSize(problemText, widthMm - 6);
      let yPos = muammoLabelY + 2.5;
      problemLines.forEach((line: string) => {
        if (yPos < heightMm - 6) {
          doc.text(line, 3, yPos);
          yPos += 5.625;
        }
      });

      // QR Code - bottom right corner
      const qrSizeMm = 12; // QR code size in mm
      const qrX = widthMm - qrSizeMm - 3; // Right side with 3mm margin
      const qrY = heightMm - qrSizeMm - 3; // Bottom with 3mm margin
      doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSizeMm, qrSizeMm);

      // Ticket ID - bottom row, left of QR code (aligned with QR code center vertically)
      doc.setFontSize(8.6); // ~15% bigger than 7.5
      doc.setFont('helvetica', 'bold');
      const ticketIdText = `Tiket ID: ${repair.repair_id}`;
      const ticketIdWidth = doc.getTextWidth(ticketIdText);
      const ticketIdX = qrX - ticketIdWidth - 2; // 2mm spacing from QR code
      const ticketIdY = qrY + qrSizeMm / 2; // Center vertically with QR code
      doc.text(ticketIdText, ticketIdX, ticketIdY);

      // Price (if available) - at bottom left
      if (repair.price !== null && repair.price !== undefined) {
        doc.setFontSize(8.6);
        doc.setFont('helvetica', 'bold');
        const priceText = `Narx: ${repair.price.toLocaleString('uz-UZ')} UZS`;
        doc.text(priceText, 3, heightMm - 3);
      }

      // Save the PDF
      doc.save(`talon-${repair.repair_id}.pdf`);
    } catch (err) {
      console.error('Error generating talon PDF:', err);
      alert('Talon yaratishda xatolik yuz berdi');
    } finally {
      setGeneratingTalon(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full px-0">
        <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto"></div>
            <div className="h-24 bg-gray-200 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (repair) {
    const stageDates = getRepairStageDates(repair, repair.diagnostic, completionRecord, statusHistory);
    const currentStatusDate =
      repair.status === 'diagnosed'
        ? stageDates.diagnosed
        : repair.status === 'fixed'
        ? stageDates.fixed
        : repair.status === 'picked'
        ? stageDates.picked
        : repair.status === 'cancelled'
        ? stageDates.cancelled
        : stageDates.accepted;

    const timelineItems = [
      { key: 'acceptedOn', date: stageDates.accepted },
      { key: 'diagnosedOn', date: stageDates.diagnosed },
      { key: 'fixedOn', date: stageDates.fixed },
      { key: 'pickedOn', date: stageDates.picked },
      { key: 'cancelledOn', date: stageDates.cancelled },
    ].filter((item): item is { key: string; date: string } => Boolean(item.date));

    return (
      <div className="w-full pb-20 lg:pb-0">
        {/* Back button only - sticky */}
        <div className="sticky top-12 md:top-14 lg:top-14 z-10 bg-white border-b border-gray-200 -mt-2 md:-mt-3 lg:-mt-3 -mx-2 md:-mx-3 lg:-mx-4 px-2 md:px-3 lg:px-4 py-3 lg:border-0 lg:pb-4 mb-4 lg:mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium min-h-[44px]"
            aria-label={t('backToCustomerIntake')}
          >
            <ArrowLeft className="w-6 h-6" />
            {t('backToCustomerIntake')}
          </button>
        </div>

        <div className="px-0">
        {/* Cyrillic Warning */}
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">{t('cyrillicWarning')}</p>
        </div>
        
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 md:gap-4 mb-6 md:mb-8">
            <button
              onClick={generatePDF}
              disabled={generating}
              className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <PrinterIcon className="w-5 h-5" />
              {generating ? t('generatingLabel') : t('downloadLabel')}
            </button>
            <button
              onClick={generateLabelPDF}
              disabled={generatingLabel}
              className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              <FileText className="w-5 h-5" />
              {generatingLabel ? 'Yorliq yaratilmoqda...' : 'Yorliq yaratish'}
            </button>
            <button
              onClick={generateTalonPDF}
              disabled={generatingTalon}
              className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Receipt className="w-5 h-5" />
              {generatingTalon ? 'Talon yaratilmoqda...' : 'Talon yaratish'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
          {/* Label Preview */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('labelPreview')}</h3>
            <LabelPreview 
              repair={repair} 
              businessName={businessProfile?.master_name || 'MasterProUZ'} 
              businessProfile={businessProfile}
            />
          </div>

          {/* Repair Details */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Header with title and created_at */}
              <div className="p-6 border-b relative">
                <h3 className="text-xl font-bold text-gray-900">Ta'mirlash tavsilotlari</h3>
                <div className="absolute top-6 right-6 text-sm text-gray-500">
                  {formatDateTime(repair.created_at)}
                </div>
              </div>

              {/* Main Content - Compact Format as per plan */}
              <div className="p-6 space-y-4">
                {/* 1. Ta'mirlash ID */}
                <div>
                  <p className="text-base text-blue-600 font-medium">Ta'mirlash ID: {repair.repair_id}</p>
                </div>

                {/* 2. Customer Name and Phone (2x bigger) */}
                <div>
                  <p className="text-2xl text-gray-900 font-medium">
                    <a
                      href={`/customer/${repair.customer.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/customer/${repair.customer.id}`);
                      }}
                      className="text-2xl text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium"
                    >
                      {repair.customer.name}
                    </a> : <a 
                      href={`tel:${repair.customer.phone}`}
                      className="text-2xl text-blue-600 hover:text-blue-700 hover:underline transition-colors font-medium"
                    >
                      {formatPhoneNumber(repair.customer.phone)}
                    </a>
                  </p>
                </div>

                {/* 3. Device */}
                <div>
                  <p className="text-base text-gray-700">
                    <span className="font-medium">Qurilma:</span> {capitalizeDeviceType(repair.device_type)}
                    {repair.device_model && ` - ${repair.device_model}`}
                    {repair.serial_number && ` - ${repair.serial_number}`}
                  </p>
                </div>

                {/* 4. Issue - Label on one line, value on new line */}
                <div>
                  <p className="text-base text-gray-700 font-medium">Muommo:</p>
                  <p className="text-base text-gray-900 mt-1">{repair.issue_description || '—'}</p>
                </div>

                {/* 5. Status */}
                <div>
                  <p className="text-base text-gray-700">
                    <span className="font-medium">Holati:</span>{' '}
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border
                      ${
                        repair.status === 'picked'
                          ? 'text-green-600 bg-green-50 border-green-200'
                          : repair.status === 'fixed'
                          ? 'text-blue-600 bg-blue-50 border-blue-200'
                          : repair.status === 'diagnosed'
                          ? 'text-blue-600 bg-blue-50 border-blue-200'
                          : repair.status === 'cancelled'
                          ? 'text-red-600 bg-red-50 border-red-200'
                          : 'text-yellow-600 bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      {repair.status === 'picked' ? 'Olib ketilgan' :
                       repair.status === 'fixed' ? 'Tayyor' :
                       repair.status === 'diagnosed' ? 'Diagnostika qilingan' :
                       repair.status === 'cancelled' ? 'Bekor qilingan' :
                       repair.status === 'accepted' ? 'Qabul qilingan' :
                       'Noma\'lum holat'}
                    </span>
                  </p>
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700">{t('currentStatusDate')}:</p>
                    <p className="text-sm text-gray-900 mt-1">
                      {currentStatusDate ? formatDateTime(currentStatusDate) : formatDateTime(repair.created_at)}
                    </p>
                  </div>
                  {repair.status === 'cancelled' && repair.cancellation_note && (
                    <div className="mt-3">
                      <p className="text-sm font-medium text-gray-700">Bekor qilish sababi:</p>
                      <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">
                        {repair.cancellation_note}
                      </p>
                    </div>
                  )}
                  {timelineItems.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">{t('repairTimeline')}:</p>
                      <div className="space-y-2">
                        {timelineItems.map((item) => (
                          <div key={item.key} className="flex items-center justify-between gap-4">
                            <span className="text-sm text-gray-600">{t(item.key)}</span>
                            <span className="text-sm text-gray-900 font-medium">{formatDateTime(item.date)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SMS Status Badge */}
              <div className="px-6 pb-6">
                <SMSStatusBadge
                  repairId={repair.repair_id}
                  customer={repair.customer}
                  businessProfile={businessProfile}
                />
              </div>

              {/* Notes History */}
              {getNotesHistory().length > 0 && (
                <div className="px-6 pb-6">
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-500 mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Eslatmalar tarixi
                    </h3>
                    <div className="space-y-4">
                      {getNotesHistory().map((item, index) => (
                        <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-1">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-gray-900">{item.stage}</span>
                                <span className="text-xs text-gray-500">
                                  {formatDateTime(item.date)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                {item.notes}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-0">
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <div className="flex gap-4 mb-4">
          <button
            onClick={() => setSearchType('customer')}
            className={`px-4 py-2 rounded-md ${
              searchType === 'customer'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('searchByCustomer')}
          </button>
          <button
            onClick={() => setSearchType('repair')}
            className={`px-4 py-2 rounded-md ${
              searchType === 'repair'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t('searchByRepairID')}
          </button>
        </div>

        <div className="relative">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={searchType === 'customer' ? t('searchCustomerName') : t('searchRepairID')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Latest Repairs */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          {searchQuery ? t('searchResults') : t('latestRepairs')}
        </h3>
        
        <div className="space-y-4">
          {latestRepairs.length > 0 ? (
            latestRepairs.map((repair) => (
              <button
                key={repair.id}
                onClick={() => navigate(`/label/${repair.repair_id}`)}
                className="w-full p-4 border rounded-lg hover:border-blue-500 transition-colors text-left"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <a
                      href={`/customer/${repair.customer.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/customer/${repair.customer.id}`);
                      }}
                      className="font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    >
                      {repair.customer.name}
                    </a>
                    <div className="text-sm text-gray-500 mt-1">
                      {capitalizeDeviceType(repair.device_type)}
                      {repair.device_model && ` - ${repair.device_model}`}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">{repair.repair_id}</div>
                </div>
              </button>
            ))
          ) : (
            <div className="text-center text-gray-500 py-8">
              {searchQuery ? t('noRepairsFound') : t('noRecentRepairs')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabelGeneration;